import React from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const TermsOfServicePage = () => {
  const lastUpdated = 'April 17, 2026';

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Header */}
      <section className="pt-20 pb-12 bg-gray-50 border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            Terms of Service
          </h1>
          <p className="text-gray-600">
            Last updated: {lastUpdated}
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-gray max-w-none">

            {/* Introduction */}
            <div className="mb-10">
              <p className="text-gray-700 leading-relaxed">
                Welcome to SuddyFlow. These Terms of Service ("Terms") govern your access to and use of the SuddyFlow
                platform, including our website, applications, and services (collectively, the "Service"). By accessing
                or using our Service, you agree to be bound by these Terms.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                SuddyFlow is operated by Suddy Buckets LLC ("Company," "we," "us," or "our"), a North Carolina limited
                liability company. Please read these Terms carefully before using our Service.
              </p>
            </div>

            {/* Section 1 */}
            <div className="mb-10">
              <h2 className="text-xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h2>
              <p className="text-gray-700 leading-relaxed">
                By creating an account, accessing, or using our Service, you acknowledge that you have read, understood,
                and agree to be bound by these Terms. If you do not agree to these Terms, you may not access or use the Service.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                You must be at least 18 years old and have the legal capacity to enter into a binding agreement to use
                our Service. If you are using the Service on behalf of a business or organization, you represent that you
                have the authority to bind that entity to these Terms.
              </p>
            </div>

            {/* Section 2 */}
            <div className="mb-10">
              <h2 className="text-xl font-bold text-gray-900 mb-4">2. Description of Service</h2>
              <p className="text-gray-700 leading-relaxed">
                SuddyFlow is a field service management platform that provides tools for customer management, job tracking,
                invoicing, expense tracking, route optimization, and related business management features. The specific
                features available to you depend on your subscription plan.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                SuddyFlow is independently developed and maintained by a single developer who also operates an active
                field service business. While we are committed to the reliability and continuity of the Service, users
                should be aware that support response times and feature development reflect the capacity of a solo
                operation. We appreciate your patience and understanding as we continue to grow and improve the platform.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                We reserve the right to modify, suspend, or discontinue any aspect of the Service at any time, including
                the availability of any feature, database, or content. We may also impose limits on certain features or
                restrict your access to parts or all of the Service without notice or liability.
              </p>
            </div>

            {/* Section 3 */}
            <div className="mb-10">
              <h2 className="text-xl font-bold text-gray-900 mb-4">3. Account Registration</h2>
              <p className="text-gray-700 leading-relaxed">
                To use certain features of the Service, you must create an account. When you create an account, you agree to:
              </p>
              <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-700">
                <li>Provide accurate, current, and complete information</li>
                <li>Maintain and promptly update your account information</li>
                <li>Keep your password secure and confidential</li>
                <li>Be responsible for all activities that occur under your account</li>
                <li>Notify us immediately of any unauthorized use of your account</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                We reserve the right to suspend or terminate your account if any information provided is inaccurate,
                false, or violates these Terms.
              </p>
            </div>

            {/* Section 4 */}
            <div className="mb-10">
              <h2 className="text-xl font-bold text-gray-900 mb-4">4. Subscription Plans and Billing</h2>
              <p className="text-gray-700 leading-relaxed">
                SuddyFlow offers various subscription plans with different features and pricing. By selecting a
                subscription plan, you agree to pay the applicable fees as described at the time of purchase.
              </p>
              <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-700">
                <li><strong>Free Trial:</strong> New accounts may be eligible for a free trial period. At the end of
                the trial, you must select a paid plan to continue using the Service.</li>
                <li><strong>Billing Cycle:</strong> Subscriptions are billed in advance on a monthly or annual basis,
                depending on your selected plan.</li>
                <li><strong>Automatic Renewal:</strong> Subscriptions automatically renew unless canceled before the
                renewal date.</li>
                <li><strong>Price Changes:</strong> We may change our prices with 30 days' notice. Price changes will
                take effect at the start of your next billing cycle.</li>
                <li><strong>Refunds:</strong> Subscription fees are non-refundable except as required by law or as
                explicitly stated in these Terms.</li>
                <li><strong>Founding Member Pricing:</strong> Accounts designated as Founding Members are eligible for
                locked pricing at the rate established at the time of their designation. Founding Member rates will not
                be subject to standard price increases and will remain in effect for the life of the subscription,
                provided the account remains active and in good standing. Founding Member status is non-transferable
                and applies to the original account only.</li>
              </ul>
            </div>

            {/* Section 5 */}
            <div className="mb-10">
              <h2 className="text-xl font-bold text-gray-900 mb-4">5. Acceptable Use</h2>
              <p className="text-gray-700 leading-relaxed">
                You agree to use the Service only for lawful purposes and in accordance with these Terms. You agree not to:
              </p>
              <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-700">
                <li>Violate any applicable laws, regulations, or third-party rights</li>
                <li>Use the Service to transmit harmful, offensive, or illegal content</li>
                <li>Attempt to gain unauthorized access to any part of the Service</li>
                <li>Interfere with or disrupt the Service or servers</li>
                <li>Use automated systems to access the Service without permission</li>
                <li>Reverse engineer, decompile, or disassemble any part of the Service</li>
                <li>Resell, sublicense, or redistribute the Service without authorization</li>
                <li>Use the Service to send spam or unsolicited communications</li>
                <li>Comply with our <Link to="/sms-policy" className="text-blue-600 hover:underline">SMS Policy</Link> when sending messages through the Service</li>
              </ul>
            </div>

            {/* Section 6 */}
            <div className="mb-10">
              <h2 className="text-xl font-bold text-gray-900 mb-4">6. Your Data</h2>
              <p className="text-gray-700 leading-relaxed">
                You retain ownership of all data, content, and information you submit to the Service ("Your Data").
                By using the Service, you grant us a limited license to use Your Data solely for the purpose of
                providing and improving the Service.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                You are responsible for the accuracy, quality, and legality of Your Data and the means by which you
                acquired it. You represent that you have all necessary rights to submit Your Data to the Service.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                We implement reasonable security measures to protect Your Data, but we cannot guarantee absolute security.
                Please see our <Link to="/privacy" className="text-blue-600 hover:underline">Privacy Policy</Link> for
                more information about how we handle your data.
              </p>
            </div>

            {/* Section 7 */}
            <div className="mb-10">
              <h2 className="text-xl font-bold text-gray-900 mb-4">7. Intellectual Property</h2>
              <p className="text-gray-700 leading-relaxed">
                The Service and its original content, features, and functionality are owned by Suddy Buckets LLC and
                are protected by copyright, trademark, and other intellectual property laws. Our trademarks, service
                marks, and logos may not be used without our prior written consent.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                "SuddyFlow," "SuddyRoute," "SuddySnap," and related names and logos are trademarks of Suddy Buckets LLC.
              </p>
            </div>

            {/* Section 8 */}
            <div className="mb-10">
              <h2 className="text-xl font-bold text-gray-900 mb-4">8. Third-Party Services</h2>
              <p className="text-gray-700 leading-relaxed">
                The Service may integrate with or contain links to third-party services (such as payment processors,
                mapping services, or accounting software). We are not responsible for the content, policies, or
                practices of any third-party services. Your use of third-party services is governed by their
                respective terms and policies.
              </p>
            </div>

            {/* Section 9 */}
            <div className="mb-10">
              <h2 className="text-xl font-bold text-gray-900 mb-4">9. Disclaimer of Warranties</h2>
              <p className="text-gray-700 leading-relaxed">
                THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR
                IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
                PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE,
                OR COMPLETELY SECURE.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                We do not provide tax, legal, or financial advice. The tax reports and calculations provided by the
                Service are for informational purposes only. You should consult with qualified professionals for
                advice specific to your situation.
              </p>
            </div>

            {/* Section 10 */}
            <div className="mb-10">
              <h2 className="text-xl font-bold text-gray-900 mb-4">10. Limitation of Liability</h2>
              <p className="text-gray-700 leading-relaxed">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, SUDDY BUCKETS LLC AND ITS OFFICERS, DIRECTORS, EMPLOYEES, AND
                AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES,
                INCLUDING LOSS OF PROFITS, DATA, OR GOODWILL, ARISING OUT OF OR RELATED TO YOUR USE OF THE SERVICE.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                OUR TOTAL LIABILITY FOR ANY CLAIMS ARISING FROM THESE TERMS OR YOUR USE OF THE SERVICE SHALL NOT
                EXCEED THE AMOUNT YOU PAID TO US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.
              </p>
            </div>

            {/* Section 11 */}
            <div className="mb-10">
              <h2 className="text-xl font-bold text-gray-900 mb-4">11. Indemnification</h2>
              <p className="text-gray-700 leading-relaxed">
                You agree to indemnify, defend, and hold harmless Suddy Buckets LLC and its officers, directors,
                employees, and agents from any claims, damages, losses, or expenses (including reasonable attorneys'
                fees) arising out of your use of the Service, your violation of these Terms, or your violation of
                any rights of another party.
              </p>
            </div>

            {/* Section 12 */}
            <div className="mb-10">
              <h2 className="text-xl font-bold text-gray-900 mb-4">12. Termination</h2>
              <p className="text-gray-700 leading-relaxed">
                You may cancel your subscription at any time through your account settings. We may suspend or
                terminate your access to the Service at any time for any reason, including if we reasonably believe
                you have violated these Terms.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                Upon termination, your right to use the Service will immediately cease. You may request an export
                of Your Data within 30 days of termination. After 30 days, we may delete Your Data in accordance
                with our data retention policies.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                <strong>Service Discontinuation:</strong> In the event that Suddy Buckets LLC elects to permanently
                discontinue the SuddyFlow platform, we will provide a minimum of 90 days written notice to all active
                subscribers via the email address associated with their account. Data export functionality will remain
                available throughout the full notice period. Any prepaid subscription fees covering periods beyond the
                discontinuation date will be refunded on a prorated basis.
              </p>
            </div>

            {/* Section 13 */}
            <div className="mb-10">
              <h2 className="text-xl font-bold text-gray-900 mb-4">13. Changes to Terms</h2>
              <p className="text-gray-700 leading-relaxed">
                We may update these Terms from time to time. We will notify you of any material changes by posting
                the new Terms on this page and updating the "Last updated" date. Your continued use of the Service
                after any changes constitutes your acceptance of the new Terms.
              </p>
            </div>

            {/* Section 14 */}
            <div className="mb-10">
              <h2 className="text-xl font-bold text-gray-900 mb-4">14. Governing Law</h2>
              <p className="text-gray-700 leading-relaxed">
                These Terms shall be governed by and construed in accordance with the laws of the State of North
                Carolina, without regard to its conflict of law provisions. Any disputes arising from these Terms
                or your use of the Service shall be resolved in the state or federal courts located in Iredell
                County, North Carolina.
              </p>
            </div>

            {/* Section 15 */}
            <div className="mb-10">
              <h2 className="text-xl font-bold text-gray-900 mb-4">15. Contact Us</h2>
              <p className="text-gray-700 leading-relaxed">
                If you have any questions about these Terms, please contact us at:
              </p>
              <div className="mt-4 bg-gray-50 p-6 rounded-lg">
                <p className="text-gray-700">
                  <strong>Suddy Buckets LLC</strong><br />
                  Email: legal@suddyflow.com<br />
                  Website: <a href="https://suddyflow.com" className="text-blue-600 hover:underline">suddyflow.com</a>
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TermsOfServicePage;